/// <reference path="../vite-env.d.ts" />
import { PhoneOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { Button, Drawer } from 'antd';
import { useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useQuery } from 'urql';
import { graphql } from '../gql';
import { internationalPhone } from '../phone';
import { whatsappLink } from '../share';
import { useSession } from '../start-app';
import './app-bar.css';

/** Help for one screen: a title and a few short steps (doc 52). */
export type HelpTopic = {
  /** A route pattern, e.g. "/meter/:meterId". */
  path: string;
  title: string;
  steps: string[];
};

const ContactDocument = graphql(`
  query HelpContact {
    currentSettings {
      contactPhone
    }
  }
`);

/**
 * The phone apps' top bar: the app's name, icon and colour, as on the home
 * screen (`__APP__`, from vite.ts), and «؟ مساعدة» for this screen's help.
 * The help sheet also holds the association's contact and the logout.
 */
export function AppBar({ help }: { help: HelpTopic[] }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const app = __APP__;
  if (!app) return null;
  const topic =
    help.find((t) => matchPath({ path: t.path, end: true }, pathname)) ??
    help[0];

  return (
    <>
      <header
        className="app-bar"
        style={{ '--app-color': app.color } as React.CSSProperties}
      >
        <img
          className="app-bar-icon"
          src={`${import.meta.env.BASE_URL}icons/${app.icon}-any-192.png`}
          alt=""
        />
        <strong className="app-bar-name">{app.name}</strong>
        <button
          type="button"
          className="app-bar-help"
          onClick={() => setOpen(true)}
        >
          <span className="app-bar-help-mark" aria-hidden>
            ؟
          </span>
          مساعدة
        </button>
      </header>
      <HelpSheet
        open={open}
        topic={topic}
        color={app.color}
        built={app.built}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function HelpSheet({
  open,
  topic,
  color,
  built,
  onClose,
}: {
  open: boolean;
  topic: HelpTopic | undefined;
  color: string;
  built: string;
  onClose: () => void;
}) {
  const session = useSession();
  const [{ data }] = useQuery({ query: ContactDocument, pause: !open });
  const settings = data?.currentSettings;
  const phone = internationalPhone(settings?.contactPhone);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="bottom"
      height="auto"
      title={topic?.title ?? 'مساعدة'}
      className="help-sheet"
      // The sheet opens outside the bar: it gets the app's colour itself.
      rootStyle={{ '--app-color': color } as React.CSSProperties}
    >
      {topic && (
        <ol className="help-steps">
          {topic.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}

      {phone && (
        <section className="help-contact">
          <div className="help-contact-title">تحتاج مساعدة؟ اتصل بالجمعية</div>
          <div className="help-contact-buttons">
            <Button
              size="large"
              className="help-whatsapp"
              icon={<WhatsAppOutlined />}
              href={whatsappLink(
                phone,
                'السلام عليكم، أحتاج مساعدة في التطبيق.',
              )}
              target="_blank"
            >
              واتساب
            </Button>
            <Button
              size="large"
              icon={<PhoneOutlined />}
              href={`tel:+${phone}`}
            >
              اتصال
            </Button>
          </div>
        </section>
      )}

      <footer className="help-footer">
        <span>
          الحساب: {session.displayName}
          <span className="help-version">
            {' '}
            · نسخة <span className="ltr-num">{built}</span>
          </span>
        </span>
        <Button type="link" onClick={session.logout}>
          تسجيل الخروج
        </Button>
      </footer>
    </Drawer>
  );
}
